/*
 * Chart operations for the Demo Environment Management page. Environment-level
 * entity. Mirrors the chart-related subset of sales-demo-env-setup's
 * `src/lib/chart.js`, adapted to use `CsqApiClient`.
 *
 * A chart is created as a draft, then a version is published so it becomes a
 * saved (non-draft) chart. To be visible in the UI it must also be attached to
 * a dashboard (see csqDashboards.js).
 */

import { resolveDashboardId } from './csqDashboards';
import { DEFAULT_TEMPLATE_ID, defaultSnapshotData } from './templates/chartTemplates';

/**
 * Creates the chart (and its initial draft).
 *   POST /api/charts/v1/projects/{environmentId}/users/me/charts
 *
 * @returns {Promise<object>} The created chart, including `id` and either a `draft`
 *   (needs `publishChartVersion`) or an already-published `latestVersion`.
 */
export function createChart(client, environmentId, { snapshotData, templateId }) {
  return client.post(`/api/charts/v1/projects/${environmentId}/users/me/charts`, { snapshotData, templateId });
}

/**
 * Publishes a version of the chart, saving the given draft.
 *   POST /api/charts/v1/projects/{environmentId}/users/me/charts/{chartId}/versions
 *
 * @returns {Promise<object>} The published version (version 1, isSaved: true).
 */
export function publishChartVersion(client, environmentId, chartId, { draftId, snapshotData }) {
  return client.post(`/api/charts/v1/projects/${environmentId}/users/me/charts/${chartId}/versions`, {
    draftIdToSave: draftId,
    snapshotData,
  });
}

/**
 * Attaches a chart to a dashboard so it becomes visible in the UI.
 *   POST /api/dashboards/v1/projects/{environmentId}/users/me/dashboards/{dashboardId}/blocks/append-chart-block
 */
export function appendChartToDashboard(client, environmentId, dashboardId, { chartId, name }) {
  return client.post(
    `/api/dashboards/v1/projects/${environmentId}/users/me/dashboards/${dashboardId}/blocks/append-chart-block`,
    { chartId, name },
  );
}

/**
 * Runs the full chart creation flow: create the chart, publish its first
 * version so it is saved, then attach it to a dashboard so it is visible in
 * the UI.
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} environmentId
 * @param {object} [config]
 * @param {string} [config.name] - Chart name (defaults to "Top devices").
 * @param {object} [config.snapshotData] - Full chart definition (overrides name).
 * @param {string} [config.templateId] - Template id (defaults to sessions_by_device).
 * @param {string} [config.dashboardId] - Target dashboard id (explicit).
 * @param {string} [config.dashboardName] - Target dashboard name (match or create).
 * @param {boolean} [config.skipDashboard] - Skip attaching the chart to a dashboard.
 * @param {(msg: string) => void} [config.log]
 * @returns {Promise<{ chartId: string, versionId: string, dashboardId: (string|null), snapshotData: object }>}
 */
export async function createChartWithVersion(client, environmentId, config = {}) {
  const log = config.log || (() => {});
  const snapshotData = config.snapshotData || defaultSnapshotData(config.name);
  const templateId = config.templateId || DEFAULT_TEMPLATE_ID;

  log(`Creating chart "${snapshotData.name}"...`);
  const chart = await createChart(client, environmentId, { snapshotData, templateId });
  const chartId = chart && chart.id;
  const draftId = chart && chart.draft && chart.draft.id;
  const existingVersion = chart && chart.latestVersion;
  if (!chartId || (!draftId && !existingVersion)) {
    throw new Error(`Unexpected create-chart response: ${JSON.stringify(chart)}`);
  }

  let version;
  if (draftId) {
    // API returned a fresh draft: publish it to save the first version.
    log(`  created chart ${chartId} (draft ${draftId}).`);
    log('Publishing chart version...');
    version = await publishChartVersion(client, environmentId, chartId, { draftId, snapshotData });
    log(`  published version ${version && version.version} (saved=${version && version.isSaved}).`);
  } else {
    // API already created and published the first version; nothing to publish.
    version = existingVersion;
    log(`  created chart ${chartId} (already published as version ${version.version}).`);
  }

  let dashboardId = null;
  if (!config.skipDashboard) {
    dashboardId = await resolveDashboardId(client, environmentId, {
      dashboardId: config.dashboardId,
      dashboardName: config.dashboardName,
      forChart: true,
      log,
    });
    log(`Attaching chart to dashboard ${dashboardId}...`);
    await appendChartToDashboard(client, environmentId, dashboardId, { chartId, name: snapshotData.name });
    log('  attached.');
  }

  return { chartId, versionId: version && version.id, dashboardId, snapshotData };
}

/**
 * Lists all saved charts for the current user in an environment.
 *   GET /api/charts/v1/projects/{environmentId}/users/me/charts?includeDrafts=false
 *
 * @returns {Promise<Array<{ id: string, name: string }>>}
 */
export async function listCharts(client, environmentId, { includeDrafts = false } = {}) {
  const payload = await client.get(`/api/charts/v1/projects/${environmentId}/users/me/charts?includeDrafts=${includeDrafts}`);
  const charts = Array.isArray(payload) ? payload : (payload && payload.items) || [];
  return charts.map((chart) => ({
    id: chart.id,
    name:
      (chart.latestVersion && chart.latestVersion.snapshotData && chart.latestVersion.snapshotData.name) ||
      (chart.draft && chart.draft.snapshotData && chart.draft.snapshotData.name) ||
      '(unnamed)',
  }));
}

/**
 * Lists all saved charts for the current user in an environment with their full
 * snapshot definition, instead of just `{id, name}`. Used to recreate a
 * project's charts on another project (see "All entity types" create-all in
 * csqEntityHandlers.js).
 *   GET /api/charts/v1/projects/{environmentId}/users/me/charts?includeDrafts=false
 *
 * @returns {Promise<Array<{ id: string, name: string, snapshotData: object }>>}
 */
export async function listChartsWithDetails(client, environmentId, { includeDrafts = false } = {}) {
  const payload = await client.get(`/api/charts/v1/projects/${environmentId}/users/me/charts?includeDrafts=${includeDrafts}`);
  const charts = Array.isArray(payload) ? payload : (payload && payload.items) || [];
  return charts
    .map((chart) => ({
      id: chart.id,
      snapshotData: (chart.latestVersion && chart.latestVersion.snapshotData) || (chart.draft && chart.draft.snapshotData) || null,
    }))
    .filter((chart) => chart.snapshotData)
    .map((chart) => ({ id: chart.id, name: chart.snapshotData.name || '(unnamed)', snapshotData: chart.snapshotData }));
}

/**
 * Deletes a single chart.
 *   DELETE /api/charts/v1/projects/{environmentId}/users/me/charts/{chartId}
 */
export function deleteChart(client, environmentId, chartId) {
  return client.del(`/api/charts/v1/projects/${environmentId}/users/me/charts/${chartId}`);
}
